import React, { useState, useEffect } from 'react';
import { Container, Grid, Typography, Box, useTheme, Button, CircularProgress, Chip } from '@mui/material';
import { useAuth } from './AuthContext';
import PricingPlan from './PricingPlan';
import axios from '../config/axiosConfig';
import staticPricingPlans from '../data/staticPricingPlans';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const Pricing = () => {
  const theme = useTheme();
  const { isAuthenticated, user } = useAuth();
  const [isMonthly, setIsMonthly] = useState(false);
  const [pricingPlans, setPricingPlans] = useState(staticPricingPlans);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPlansAndPromotions = async () => {
      try {
        const [plansResponse, promotionsResponse] = await Promise.all([
          axios.get('/api/stripe/plans'),
          axios.get('/api/promotions/active')
        ]);

        const updatedPlans = staticPricingPlans.map(plan => {
          const matchedPlans = plansResponse.data.filter(p => p.name.trim().toLowerCase() === plan.title.trim().toLowerCase());

          matchedPlans.forEach(dynamicPlan => {
            if (dynamicPlan.interval === 'month') {
              plan.monthlyPrice = `$${parseFloat(dynamicPlan.price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/month`;
              plan.monthlyId = dynamicPlan.id;
            } else if (dynamicPlan.interval === 'year') {
              plan.yearlyPrice = `$${parseFloat(dynamicPlan.price / 12).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/month`;
              plan.yearlyId = dynamicPlan.id;
            }
          });

          plan.activePromotions = promotionsResponse.data.filter(promotion =>
            promotion.eligiblePriceIds.includes(plan.monthlyId) || promotion.eligiblePriceIds.includes(plan.yearlyId)
          );

          return plan;
        });

        setPricingPlans(updatedPlans);
      } catch (error) {
        console.error('Error fetching pricing plans and promotions:', error);
      }
    };

    fetchPlansAndPromotions();
  }, []);

  const handlePlanSelect = async (plan) => {
    setIsLoading(true);
    const priceId = isMonthly ? plan.monthlyId : plan.yearlyId;
    if (isAuthenticated) {
      try {
        const { data } = await axios.post('/api/stripe/create-checkout-session', {
          priceId: priceId,
          userId: user._id
        });
        const sessionUrl = data.url;
        window.location.href = sessionUrl;
      } catch (err) {
        console.error('Error:', err);
        alert('Failed to start checkout session.');
        setIsLoading(false);
      }
    } else {
      window.location.href = `/signup?plan=${plan.title.toLowerCase()}&priceId=${priceId}`;
    }
  };

  const renderButton = (plan) => {
    const handleFreePlanSelect = () => {
      if (plan.isFree && isAuthenticated) {
        window.location.href = '/';
      } else {
        handlePlanSelect(plan);
      }
    };

    if (isAuthenticated && user.plan.name.toLowerCase() === plan.title.toLowerCase() && !plan.isFree) {
      return null;
    }

    return (
      <Button
        variant="contained"
        color="primary"
        onClick={handleFreePlanSelect}
        disabled={isLoading}
        sx={{ mt: 2, position: 'relative' }}
      >
        {isLoading ? (
          <CircularProgress size={24} sx={{ color: 'white' }} />
        ) : (
          plan.btn
        )}
      </Button>
    );
  };

  return (
    <Container sx={{ mt: 6, mb: 6 }}>
      <Box sx={{ padding: theme.spacing(4) }}>
        <Typography
          variant="h3"
          component="h2"
          align="center"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(to right, #fff 0%, #c7c7c7 100%)'
              : 'linear-gradient(to right, #1976d2 0%, #1565c0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Choose Your Plan
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 6,
            gap: 1.5,
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              borderRadius: '50px',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.06)'
                : 'rgba(0, 0, 0, 0.06)',
              padding: '4px',
            }}
          >
            <Button
              onClick={() => setIsMonthly(false)}
              disableElevation
              sx={{
                borderRadius: '50px',
                px: { xs: 2.5, sm: 3 },
                py: 1,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                fontWeight: 600,
                textTransform: 'none',
                minWidth: { xs: 90, sm: 110 },
                backgroundColor: !isMonthly ? theme.palette.primary.main : 'transparent',
                color: !isMonthly ? '#fff' : theme.palette.text.secondary,
                transition: 'all 0.25s ease-in-out',
                '&:hover': {
                  backgroundColor: !isMonthly
                    ? theme.palette.primary.dark
                    : theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Annual
            </Button>
            <Button
              onClick={() => setIsMonthly(true)}
              disableElevation
              sx={{
                borderRadius: '50px',
                px: { xs: 2.5, sm: 3 },
                py: 1,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                fontWeight: 600,
                textTransform: 'none',
                minWidth: { xs: 90, sm: 110 },
                backgroundColor: isMonthly ? theme.palette.primary.main : 'transparent',
                color: isMonthly ? '#fff' : theme.palette.text.secondary,
                transition: 'all 0.25s ease-in-out',
                '&:hover': {
                  backgroundColor: isMonthly
                    ? theme.palette.primary.dark
                    : theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Monthly
            </Button>
          </Box>
          {!isMonthly && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            >
              Save 20% with annual billing
            </Typography>
          )}
        </Box>

        <Grid container spacing={4}>
          {pricingPlans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={plan.title}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: theme.spacing(4),
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  border: index === 2 ? `3px solid ${theme.palette.primary.main}` : 'none',
                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  position: 'relative',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {index === 2 && (
                  <Chip
                    label="Best Value"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      backgroundColor: theme.palette.primary.main,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      height: 26,
                      px: 1,
                    }}
                  />
                )}
                <PricingPlan
                  price={isMonthly ? plan.monthlyPrice : plan.yearlyPrice}
                  title={plan.title}
                  description={plan.description}
                  features={plan.features.map((feature, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleRoundedIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      {feature}
                    </Box>
                  ))}
                  btn={plan.btn}
                  isMonthly={isMonthly}
                  activePromotions={plan.activePromotions}
                  plan={plan}
                />
                {renderButton(plan)}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default Pricing;